import React, { useState, useEffect } from 'react';
import { 
  Menu, X, ArrowLeft, Play, Shield, Users, Upload, Share2, 
  Clock, Heart, FileText, Video, Image, Mic, Lock, 
  Smartphone, Monitor, Cloud, Settings, Bell, ChevronRight,
  CheckCircle, AlertCircle, UserCheck, Archive
} from 'lucide-react';
import { NavLink } from 'react-router-dom';

const FeaturesPage = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('security');
  const [isVisible, setIsVisible] = useState<any>({});

  // Intersection Observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev:any) => ({
              ...prev,
              [entry.target.id]: true
            }));
          }
        });
      },
      { threshold: 0.1 }
    );

    // Observe all sections
    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  const mainFeatures = [
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Multi-Media Support",
      description: "Store and share videos, voice messages, images, and documents, ensuring your digital legacy includes every meaningful format for future generations.",
      features: [
        "Video recordings up to 4K quality",
        "Voice messages and audio files",
        "High-resolution photos and images", 
        "Documents (PDF, Word, Excel, etc.)",
        "Custom file formats supported"
      ]
    },
    {
      icon: <Share2 className="w-8 h-8" />,
      title: "Seamless File Sharing",
      description: "Easily share vital documents, messages, or memories with selected family members or friends, ensuring your legacy lives on.",
      features: [
        "Selective sharing with specific recipients",
        "Time-based sharing controls",
        "Group sharing capabilities",
        "Access permission management",
        "Sharing activity tracking"
      ]
    },
    {
      icon: <Archive className="w-8 h-8" />,
      title: "Legacy Distribution Afterlife",
      description: "bETERNAL detects prolonged absence, preassigns heirs, and sends emails ensuring your digital assets are securely transferred as planned.",
      features: [
        "Automatic inactivity detection",
        "Pre-configured heir assignments",
        "Secure email notifications",
        "Gradual access release",
        "Emergency contact system"
      ]
    },
    {
      icon: <Lock className="w-8 h-8" />,
      title: "Privacy & Control",
      description: "You decide who accesses your files and when, ensuring complete control over your digital assets and personal information.",
      features: [
        "Granular permission controls",
        "Individual file access settings",
        "Time-delayed access options",
        "Revokable permissions",
        "Privacy-first design"
      ]
    }
  ];

  const securityFeatures = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Advanced Encryption",
      description: "Enterprise-grade encryption protects all your files"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "Secure Storage",
      description: "Your data is stored in military-grade secure facilities"
    },
    {
      icon: <UserCheck className="w-6 h-6" />,
      title: "Multi-Factor Authentication",
      description: "Additional security layers protect your account"
    },
    {
      icon: <AlertCircle className="w-6 h-6" />,
      title: "Activity Monitoring",
      description: "Real-time monitoring detects unusual activity"
    }
  ];

  const platformFeatures = [
    {
      icon: <Smartphone className="w-6 h-6" />,
      title: "Mobile App",
      description: "Full-featured iOS and Android applications"
    },
    {
      icon: <Monitor className="w-6 h-6" />,
      title: "Desktop Access",
      description: "Complete web-based platform access"
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud Sync",
      description: "Automatic synchronization across all devices"
    },
    {
      icon: <Settings className="w-6 h-6" />,
      title: "Easy Management",
      description: "Intuitive interface for managing your legacy"
    }
  ];

  const tabContent:any = {
    security: {
      title: "Enterprise-Grade Security",
      description: "Your memories and digital assets are protected with the highest level of security available.",
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=600&h=400&fit=crop",
      features: securityFeatures
    },
    platform: {
      title: "Multi-Platform Access",
      description: "Access your digital legacy from anywhere, on any device, at any time.",
      image: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=400&fit=crop",
      features: platformFeatures
    }
  };

  const howItWorks = [
    {
      step: "1",
      title: "Create Your Account",
      description: "Sign up using your email to begin your Beternal journey. It only takes a few minutes.",
      icon: <UserCheck className="w-8 h-8" />
    },
    {
      step: "2", 
      title: "Assign Heirs",
      description: "Assign your trusted loved ones to manage your memories, will, assets",
      icon: <Users className="w-8 h-8" />
    },
    {
      step: "3",
      title: "Upload Your Legacy Files", 
      description: "Securely upload important documents, messages, voice notes, and memories you want to preserve.",
      icon: <Upload className="w-8 h-8" />
    },
    {
      step: "4",
      title: "Share With Heirs",
      description: "Select and share access with family, friends, and loved ones so they can be a part of your digital legacy.",
      icon: <Share2 className="w-8 h-8" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 font-sans">
     

      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-lg border-b border-orange-100 shadow-sm animate-slideDown">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3 animate-slideInLeft">
              <NavLink to={'/'} >
                <img src="images/log.svg" className="hover:scale-105 transition-transform duration-300" />
              </NavLink>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden lg:flex items-center space-x-10 animate-slideInRight">
              <a href="/" className="hover:text-orange-500 text-gray-700 font-semibold text-lg pb-1 hover:scale-105 transition-all duration-300">Home</a>
              <a href="features" className="text-orange-500 border-b-2 border-orange-500 transition-colors text-lg font-medium">Features</a>
              <a href="pricing" className="text-gray-700 hover:text-orange-500 transition-all duration-300 text-lg font-medium hover:scale-105">Package</a>
              <a href="contact-us" className="text-gray-700 hover:text-orange-500 transition-all duration-300 text-lg font-medium hover:scale-105">Contact</a>
              <a href='sign-in' className="text-orange-500 hover:text-orange-600 transition-all duration-300 text-lg font-medium hover:scale-105">Sign in</a>
              <a href='register' className="bg-orange-500 text-white px-8 py-3 rounded-xl hover:bg-orange-600 transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105">
                Sign up
              </a>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 hover:scale-110 transition-transform duration-300"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white/95 backdrop-blur-lg border-t border-orange-100 shadow-lg animate-slideDown">
            <div className="px-6 py-6 space-y-4">
              <a href="/" className="block py-3 text-grey-700 font-semibold text-xl hover:text-orange-500 hover:scale-105 transition-all duration-300">Home</a>
              <a href="features" className="block py-3 text-orange-500 text-xl">Features</a>
              <a href="pricing" className="block py-3 text-gray-700 text-xl hover:text-orange-500 hover:scale-105 transition-all duration-300">Package</a>
              <a href="contact-us" className="block py-3 text-gray-700 text-xl hover:text-orange-500 hover:scale-105 transition-all duration-300">Contact</a>
              <div className="flex flex-col space-y-3 pt-4">
                <a href='/sign-in' className="text-orange-500 text-xl font-medium text-left hover:scale-105 transition-transform duration-300">Sign in</a>
                <a href='/register' className="bg-orange-500 text-white px-6 py-3 rounded-xl text-lg font-semibold w-full hover:bg-orange-600 hover:scale-105 transition-all duration-300">Sign up</a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight animate-fadeInUp">
            Powerful Features for Your
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-500 animate-slideInRight stagger-1">
              Digital Legacy
            </span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-12 leading-relaxed animate-fadeInUp stagger-2">
            Discover how bETERNAL helps you preserve, protect, and share what matters most with the people you love
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center animate-fadeInUp stagger-3">
            <a href='/register' className="bg-orange-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg min-w-[240px] animate-pulse-slow">
              Get Started For Free
            </a>
            {/* <div className="flex items-center space-x-3 text-gray-600 hover:text-orange-500 transition-all duration-300 cursor-pointer group">
              <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                <Play className="w-5 h-5 ml-1 group-hover:scale-110 transition-transform duration-300" />
              </div>
              <span className="text-lg font-medium group-hover:scale-105 transition-transform duration-300">See how it works</span>
            </div> */}
          </div>
        </div>
      </section>

      {/* Main Features Grid */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div 
            id="features-intro"
            data-animate
            className={`text-center mb-16 scroll-animate ${isVisible['features-intro'] ? 'visible' : ''}`}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Everything You Need to Preserve Your Legacy
            </h2>
            <p className="text-lg text-gray-600">
              Comprehensive tools designed to safeguard your most precious memories and important information
            </p>
          </div>

          <div 
            id="features-grid"
            data-animate
            className={`grid lg:grid-cols-2 gap-12 scroll-animate ${isVisible['features-grid'] ? 'visible' : ''}`}
          >
            {mainFeatures.map((feature, index) => (
              <div 
                key={index} 
                className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-500 border border-gray-100 hover-lift animate-scaleIn"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-orange-400 to-red-500 rounded-2xl flex items-center justify-center text-white mb-6 shadow-lg hover:scale-110 hover:rotate-6 transition-all duration-300 animate-bounceIn">
                  {feature.icon}
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 hover:text-orange-500 transition-colors duration-300">{feature.title}</h3>
                <p className="text-base text-gray-600 leading-relaxed mb-6">{feature.description}</p>
                
                <ul className="space-y-3">
                  {feature.features.map((item, itemIndex) => (
                    <li 
                      key={itemIndex} 
                      className="flex items-start space-x-3 animate-slideInLeft"
                      style={{ animationDelay: `${(index * 0.2) + (itemIndex * 0.1) + 0.5}s` }}
                    >
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0 hover:scale-125 transition-transform duration-300" />
                      <span className="text-sm text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Platform Tabs */}
      <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-orange-50">
        <div className="max-w-7xl mx-auto">
          <div 
            id="tabs-intro"
            data-animate
            className={`text-center mb-16 scroll-animate ${isVisible['tabs-intro'] ? 'visible' : ''}`}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Built for Security & Accessibility
            </h2>
          </div>

          {/* Tab Navigation */}
          <div 
            id="tab-nav"
            data-animate
            className={`flex justify-center mb-12 scroll-animate ${isVisible['tab-nav'] ? 'visible' : ''}`}
          >
            <div className="bg-white rounded-2xl p-2 shadow-lg border border-gray-200 animate-scaleIn">
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveTab('security')}
                  className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${
                    activeTab === 'security'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-orange-500'
                  }`}
                >
                  Security Features
                </button>
                <button
                  onClick={() => setActiveTab('platform')}
                  className={`px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${
                    activeTab === 'platform'
                      ? 'bg-orange-500 text-white shadow-md'
                      : 'text-gray-600 hover:text-orange-500'
                  }`}
                >
                  Platform Access
                </button>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div 
            id="tab-content"
            data-animate
            className={`grid lg:grid-cols-2 gap-16 items-center scroll-animate ${isVisible['tab-content'] ? 'visible' : ''}`}
          >
            <div className="tab-transition">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 animate-slideInLeft">
                {tabContent[activeTab].title}
              </h3>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed animate-slideInLeft stagger-1">
                {tabContent[activeTab].description}
              </p>

              <div className="grid sm:grid-cols-2 gap-6">
                {tabContent[activeTab].features.map((feature:any, index:any) => (
                  <div 
                    key={index} 
                    className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 animate-scaleIn"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-orange-500 mb-4 hover:bg-orange-200 hover:scale-110 transition-all duration-300">
                      {feature.icon}
                    </div>
                    <h4 className="font-bold text-gray-900 mb-2 hover:text-orange-500 transition-colors duration-300">{feature.title}</h4>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:order-first tab-transition">
              <div className="relative animate-scaleIn">
                <img 
                  src={tabContent[activeTab].image}
                  alt={tabContent[activeTab].title}
                  className="w-full h-80 object-cover rounded-3xl shadow-2xl hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-orange-500/20 to-red-500/20 rounded-3xl"></div>
                <div className="absolute inset-0 rounded-3xl bg-black/0 hover:bg-black/10 transition-all duration-300"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div 
            id="how-it-works-intro"
            data-animate
            className={`text-center mb-16 scroll-animate ${isVisible['how-it-works-intro'] ? 'visible' : ''}`}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              How It Works
            </h2>
            <p className="text-lg text-gray-600">
              With few clicks your memories are preserved and bETERNAL handle the rest
            </p>
          </div>

          <div 
            id="how-it-works-steps"
            data-animate
            className={`grid md:grid-cols-2 lg:grid-cols-4 gap-8 scroll-animate ${isVisible['how-it-works-steps'] ? 'visible' : ''}`}
          >
            {howItWorks.map((step, index) => (
              <div 
                key={index} 
                className="text-center animate-fadeInUp hover-lift"
                style={{ animationDelay: `${index * 0.2}s` }}
              >
                <div className="relative mb-8">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-red-500 rounded-full flex items-center justify-center text-white mx-auto shadow-xl hover:shadow-2xl hover:scale-110 transition-all duration-300 animate-bounceIn">
                    {step.icon}
                  </div>
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg animate-rotateIn hover:rotate-12 transition-transform duration-300">
                    {step.step}
                  </div>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gradient-to-r from-orange-300 to-gray-200 transform -translate-y-1/2 animate-slideInRight"></div>
                  )}
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3 hover:text-orange-500 transition-colors duration-300">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <a href='/regsiter' className="bg-orange-500 text-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-orange-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl animate-pulse-slow">
              Get Started For Free Now!
            </a>
          </div>
        </div>
      </section>

      {/* Demo Video Section */}
      <section 
        id="demo-video"
        data-animate
        className={`py-20 px-6 lg:px-8 bg-gradient-to-r from-orange-500 to-red-500 scroll-animate ${isVisible['demo-video'] ? 'visible' : ''}`}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 animate-fadeInUp">
            See How It Works - Behind The Scene
          </h2>
          <p className="text-lg text-white/90 mb-12 animate-fadeInUp stagger-1">
            Watch our step-by-step demonstration of how bETERNAL protects and preserves your digital legacy
          </p>

          <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 animate-scaleIn stagger-2">
            <div className="aspect-video bg-white/20 rounded-2xl flex items-center justify-center group cursor-pointer">
              <div className="w-20 h-20 bg-white/30 rounded-full flex items-center justify-center hover:bg-white/40 transition-all duration-300 group-hover:scale-125 animate-float">
                <Play className="w-8 h-8 text-white ml-1" />
              </div>
            </div>
            <div className="absolute bottom-4 left-4 text-white text-sm animate-slideInLeft">
              2:28
            </div>
          </div>

          <div className="mt-12">
            <a href='/register' className="bg-white text-orange-500 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 shadow-xl hover:shadow-2xl animate-bounceIn stagger-3">
              Get Started For Free Now!
            </a>
          </div>
        </div>
      </section>

     {/* Footer */}
     <footer 
        id="footer"
        data-animate
        className={`bg-gray-900 text-white py-20 px-6 lg:px-8 scroll-animate ${isVisible['footer'] ? 'visible' : ''}`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="animate-slideInLeft">
              <div className="flex items-center space-x-3 mb-6 hover:scale-105 transition-transform duration-300 group">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center animate-pulse-slow group-hover:rotate-12 transition-transform duration-300">
                  <span className="text-black font-bold text-lg">b</span>
                </div>
                <span className="text-2xl font-bold">ETERNAL</span>
              </div>
              <p className="text-gray-400 text-lg font-light">YOUR LEGACY, YOUR WAY.</p>
            </div>

            <div className="animate-slideInLeft stagger-1">
              <h4 className="font-bold text-xl mb-6">Site Link</h4>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <a href="/" className="hover:text-white transition-all duration-300 text-lg hover:scale-105 inline-block transform hover:translate-x-2">
                    Home
                  </a>
                </li>
                <li>
                  <a href="features" className="hover:text-white transition-all duration-300 text-lg hover:scale-105 inline-block transform hover:translate-x-2">
                    Features
                  </a>
                </li>
                <li>
                  <a href="pricing" className="hover:text-white transition-all duration-300 text-lg hover:scale-105 inline-block transform hover:translate-x-2">
                    Packages
                  </a>
                </li>
                <li>
                  <a href="contact-us" className="hover:text-white transition-all duration-300 text-lg hover:scale-105 inline-block transform hover:translate-x-2">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div className="animate-slideInLeft stagger-2">
              <h4 className="font-bold text-xl mb-6">Legal</h4>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <a href="#" className="hover:text-white transition-all duration-300 text-lg hover:scale-105 inline-block transform hover:translate-x-2">
                    Terms & Conditions
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-all duration-300 text-lg hover:scale-105 inline-block transform hover:translate-x-2">
                    Help & Support
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-all duration-300 text-lg hover:scale-105 inline-block transform hover:translate-x-2">
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>

            <div className="animate-slideInLeft stagger-3">
              <h4 className="font-bold text-xl mb-6">Social Media</h4>
              <div className="flex space-x-4">
                {/* Facebook */}
                <a
                  href="https://www.facebook.com/share/18tWm4dH8V/?mibextid=wwXIfr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-600 rounded-2xl hover:scale-110 transition-all duration-300 cursor-pointer flex items-center justify-center hover:bg-blue-700 hover:rotate-12 animate-bounceIn stagger-1 group"
                  aria-label="Visit our Facebook page"
                >
                  <svg className="w-6 h-6 text-white group-hover:scale-125 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>

                {/* LinkedIn */}
                <a
                  href="https://www.linkedin.com/company/beternal/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-blue-500 rounded-2xl hover:scale-110 transition-all duration-300 cursor-pointer flex items-center justify-center hover:bg-blue-600 hover:rotate-12 animate-bounceIn stagger-2 group"
                  aria-label="Visit our LinkedIn page"
                >
                  <svg className="w-6 h-6 text-white group-hover:scale-125 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </a>

                {/* YouTube */}
                <a
                  href="https://www.youtube.com/@beternal4543?si=LY1iOIxF-jzEaoY9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 bg-red-600 rounded-2xl hover:scale-110 transition-all duration-300 cursor-pointer flex items-center justify-center hover:bg-red-700 hover:rotate-12 animate-bounceIn stagger-3 group"
                  aria-label="Visit our YouTube channel"
                >
                  <svg className="w-6 h-6 text-white group-hover:scale-125 transition-transform duration-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-16 pt-8 text-center text-lg text-gray-400 font-light animate-fadeInUp">
            <div className="hover:text-white transition-colors duration-300">
              bETERNAL ® | © 2025 all rights reserved
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FeaturesPage;